import React from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableRow,
  TableCell,
  FormControl,
  Select,
  MenuItem
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { paymentRhythmDisplayNames } from '../../constants';
import { borderRadius } from '../../assets/styles/themes/tokens';
import PromoCode from '../../routes/SinglePagePayment/PromoCode';
import { currencyFormat } from '../../utils/format';
import { PaymentPeriod } from '@klab-berlin/api-sdk/lib/types/common';
import {
  DiscountInformation,
  LicenceModel,
  SubscriptionResponse
} from '@klab-berlin/api-sdk/lib/types/responses/Billing';
import { User } from '../../types/User';
import PricingDetails, { PricingDataProps } from './PricingDetails';
import useScreenSize from '../../utils/useScreenSize';
import { customSizes } from '../../assets/styles/themes/tokens';
interface SubscriptionPaymentDetailsProps {
  hasSubscriptionPreviewData: boolean;
  subscriptionUpgradePreview: any;
  paymentRhythms: PaymentPeriod[];
  selectedPaymentRhythm: PaymentPeriod;
  setSelectedPaymentRhythm: (paymentPeriod: PaymentPeriod) => void;
  hidePromoCode: boolean;
  licence: LicenceModel;
  promoCode: string;
  handlePromoCodeValidated: (discountInformation: DiscountInformation, promoCode: string) => void;
  handlePromoCodeActiveLayoutInit: () => void;
  amountDueToday: string;
  previousPrice: number | undefined;
  currentPrice: number;
  user?: User;
  currentSubscription?: SubscriptionResponse;
  promoCodeDiscountInfo?: DiscountInformation;
}

const SubscriptionPaymentDetails: React.FC<SubscriptionPaymentDetailsProps> = ({
  hasSubscriptionPreviewData,
  subscriptionUpgradePreview,
  paymentRhythms,
  selectedPaymentRhythm,
  setSelectedPaymentRhythm,
  hidePromoCode,
  licence,
  promoCode,
  handlePromoCodeValidated,
  handlePromoCodeActiveLayoutInit,
  amountDueToday,
  previousPrice,
  currentPrice,
  user,
  currentSubscription,
  promoCodeDiscountInfo,
}) => {
  const { t } = useTranslation();
  const { isLgScreen, isMdScreen } = useScreenSize();
  const isDesktopScreen = isMdScreen || isLgScreen;
  const { licenceModelId, price: licencePrice } = licence;
  const paymentPeriod = currentSubscription?.paymentPeriod?.type;

  const renderPrices = () => {
    const effectivePrice = promoCodeDiscountInfo?.newPricePerMonth ?? licencePrice;
    let pricingData: PricingDataProps[] = [];
    if (user?.isBibliothekUser && hasSubscriptionPreviewData) {
      pricingData = [
        ...(subscriptionUpgradePreview.payPeriod !== 12
          ? [{ label: t('New monthly amount'), price: currencyFormat(effectivePrice),
            dataTestId: 'new-monthly-amount' }]
          : []),
        {
          label: subscriptionUpgradePreview.payPeriod === 12
            ? t('Additional total costs')
            : t('Remaining total amount'),
          price: currencyFormat(subscriptionUpgradePreview.totalAmount),
          dataTestId: 'remaining-total-amount'
        },
        {
          label: t('Amount Due Today'),
          price: currencyFormat(subscriptionUpgradePreview.amountDueToday),
          dataTestId: 'amount-due-today'
        },
      ];
    } else if (user?.isCourseUser && hasSubscriptionPreviewData) {
      pricingData = [
        {
          label: t('New monthly amount'),
          price: currencyFormat(effectivePrice),
          dataTestId: 'new-monthly-amount'
        },
        {
          label: t('Remaining total amount'),
          price: currencyFormat(subscriptionUpgradePreview.totalAmount),
          dataTestId: 'remaining-total-amount'
        },
        {
          label: t('Amount Due Today'),
          price: currencyFormat(subscriptionUpgradePreview.amountDueToday),
          dataTestId: 'amount-due-today'
        },
      ];

    } else {
      pricingData = [
        {
          label: t('Amount Due Today'),
          price: amountDueToday,
          dataTestId: 'amount-due-today'
        },
        {
          label: t('Total'),
          price: currencyFormat(currentPrice),
          previousPrice: previousPrice ? currencyFormat(previousPrice) : undefined,
          dataTestId: 'total-price'
        },
      ];
    }

    return <PricingDetails pricingData={pricingData} />;
  };

  return (
    <Table>
      <TableBody>
        <TableRow>
          <TableCell sx={{ paddingX: 0 }}>
            <Typography variant='body2'>
              {t('Payment rhythm')}
            </Typography>
          </TableCell>
          <TableCell
            sx={{
              paddingX: 0,
              width: isDesktopScreen
                ? customSizes.paymentRhythmSelect.width.desktop
                : customSizes.paymentRhythmSelect.width.mobile
            }}
            align={hasSubscriptionPreviewData ? 'right' : 'inherit'}
          >
            {
              (hasSubscriptionPreviewData && paymentPeriod) ?
                <Typography variant='body2' data-testid='payment-rhythm'>
                  {t(paymentRhythmDisplayNames[paymentPeriod])}
                </Typography>
                : <FormControl variant='outlined' size='small' fullWidth>
                  <Select
                    data-testid='payment-rhythm-select'
                    id='payment-rhythm-select'
                    defaultValue={paymentRhythms[0]}
                    onChange={(e) => setSelectedPaymentRhythm(e.target.value as any)}
                    sx={{ borderRadius: borderRadius.sm }}
                  >
                    {paymentRhythms.map((rhythm) => (
                      <MenuItem key={rhythm} value={rhythm}>
                        {t(paymentRhythmDisplayNames[rhythm])}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
            }
          </TableCell>
        </TableRow>
        {!hidePromoCode && (
          <TableRow>
            <PromoCode
              licenceId={licenceModelId}
              initPromoCode={promoCode}
              paymentRhythm={selectedPaymentRhythm}
              onPromoCodeValidation={handlePromoCodeValidated}
              onActiveLayoutInit={handlePromoCodeActiveLayoutInit}
            />
          </TableRow>
        )}
        {renderPrices()}
      </TableBody>
    </Table>
  );
};

export default SubscriptionPaymentDetails;
